import { FC, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { Info } from "@livechat/design-system-icons";
import { Alert, Button, Icon, SpacingToken, Text } from "@livechat/design-system-react-components";

import { EmptyState } from "components/empty-state/EmptyState";
import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { ToastContent, getToastContent } from "constants/toasts";
import { IWithBreakpointsProps, withBreakpoints } from "helpers/withBreakpoints";
import { UpsellAlert } from "routes/subscription/UpsellAlert";
import { EmailDomainActions, selectDomains } from "store/entities/email-domains";
import { selectLicenseId, selectMaxEmailDomains } from "store/entities/license";
import { selectCanManageCustomMailboxes } from "store/entities/mailboxes/selectors";
import { ToastVariant, ToastsActions } from "store/features/toasts";
import { SettingsViewActions } from "store/views/settings";
import { withVar } from "themes/helpers";

import { NotAllowedBanner } from "../NotAllowedBanner";
import { trackEmailSectionOpened } from "../analytics";
import { DOMAINS_MODAL_HASH, EmailSettingsRoutes, EmailSettingsSubsection } from "../routes";

import { ConfirmCloseModal } from "./ConfirmCloseModal";
import DeleteDomainModal from "./DeleteDomainModal";
import DomainSetupModal, { DomainSetupAction, DomainSetupActionType } from "./DomainSetupModal";
import { Headline } from "./Headline";
import {
  trackAddDomainModalClosed,
  trackAddDomainModalOpened,
  trackBackToAddingClicked,
  trackDeleteDomainModalOpened,
  trackIllDoItLaterClicked,
  trackOpenConfirmationModal,
} from "./analytics";
import { DomainsList } from "./domains-list/DomainsList";

const DomainsSectionComponent: FC<IWithBreakpointsProps> = ({ isMobile }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const domains = useSelector(selectDomains);
  const licenseId = useSelector(selectLicenseId);
  const canManageDomains = useSelector(selectCanManageCustomMailboxes);
  const maxEmailDomains = useSelector(selectMaxEmailDomains);

  const [modalAction, setModalAction] = useState<DomainSetupAction | null>(null);
  const [deleteDomainId, setDeleteDomainId] = useState<string | null>(null);
  const [confirmCloseModal, setConfirmCloseModal] = useState<boolean>(false);
  const domainIdRef = useRef<string | null>(null);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const domainToDelete = domains.find(({ id }: any) => id === deleteDomainId);
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fullyCustomDomains = domains.filter(({ dns }: any) => !!dns);
  const maxDomainsReached = fullyCustomDomains.length >= maxEmailDomains;

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const createCustomDomain = (name: string, skipRetry?: boolean) =>
    dispatch(EmailDomainActions.create({ value: { name }, requestSource: "addCustomDomainModal", skipRetry }));

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const verifyCustomDomain = (domainId: string, skipRetry?: boolean) =>
    dispatch(EmailDomainActions.verify({ id: domainId, skipRetry }));

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const deleteCustomDomain = (domainId: string) => dispatch(SettingsViewActions.deleteEmailDomain(domainId));

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const onConfigurationStringCopy = (recordType: string, field: string) =>
    dispatch(SettingsViewActions.domainConfigurationCopied({ recordType, field }));

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const showCreateEmailDomainSuccessToast = () =>
    dispatch(
      ToastsActions.createToast({
        content: getToastContent(ToastContent.EMAIL_DOMAIN_CREATE_SUCCESS),
        variant: ToastVariant.Success,
      })
    );

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const onModalClose = () => {
    trackAddDomainModalClosed();
    if (modalAction?.type && modalAction.type === "finish-setup") {
      setConfirmCloseModal(true);
      trackOpenConfirmationModal();
      domainIdRef.current = modalAction.domainId;
      setModalAction(null);
    } else {
      setModalAction(null);
      if (history.location.hash === DOMAINS_MODAL_HASH) {
        history.push(EmailSettingsRoutes.Domains);
      }
    }
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const closeConfirmationModal = () => {
    setModalAction({ type: DomainSetupActionType.FinishSetup, domainId: domainIdRef.current });
    trackBackToAddingClicked();
    setConfirmCloseModal(false);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const confirmClose = () => {
    setConfirmCloseModal(false);
    trackIllDoItLaterClicked();
    setModalAction(null);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleNewDomain = (source: "empty_state" | "main") => {
    trackAddDomainModalOpened(source);
    setModalAction({ type: DomainSetupActionType.SelectType });
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const openDeleteModal = (domainId: string) => {
    trackDeleteDomainModalOpened();
    setDeleteDomainId(domainId);
  };

  useEffect(() => {
    trackEmailSectionOpened(EmailSettingsSubsection.Domains);
  }, []);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const closeDeleteModal = () => setDeleteDomainId(null);
  const handleDomainDeletion = useCallback(
    (domainId: string) => {
      deleteCustomDomain(domainId);
      setDeleteDomainId(null);
    },
    [deleteCustomDomain]
  );

  useEffect(() => {
    if (history.location.hash === DOMAINS_MODAL_HASH && canManageDomains) {
      setModalAction({ type: DomainSetupActionType.SelectType });
    }
  }, [canManageDomains, history.location.hash]);

  useEffect(() => {
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (deleteDomainId !== null && !domains.find(({ id }: any) => id === deleteDomainId)) {
      setDeleteDomainId(null);
    }
  }, [deleteDomainId, domains]);

  return (
    <>
      {deleteDomainId !== null && domainToDelete && (
        <DeleteDomainModal onDomainDelete={handleDomainDeletion} onClose={closeDeleteModal} domainId={deleteDomainId} />
      )}
      {confirmCloseModal && <ConfirmCloseModal onClose={closeConfirmationModal} confirmClose={confirmClose} />}
      {modalAction !== null && (
        <DomainSetupModal
          modalAction={modalAction}
          setModalAction={setModalAction}
          createCustomDomain={createCustomDomain}
          verifyCustomDomain={verifyCustomDomain}
          onConfigurationStringCopy={onConfigurationStringCopy}
          onClose={onModalClose}
          licenseId={licenseId}
          // @ts-expect-error TS(2322): Type 'unknown' is not assignable to type 'IEmailDo... Remove this comment to see the full error message
          domains={domains}
          showCreateEmailDomainSuccessToast={showCreateEmailDomainSuccessToast}
        />
      )}
      <Headline />
      <Flex column gap={withVar(SpacingToken.Spacing8)}>
        {domains.length === 0 ? (
          <Flex justifyContent="center">
            <EmptyState
              title="No domain added"
              dataTestId="empty-state-domains"
              icon={<Icon source={Info} size="xxxlarge" />}
              actions={
                <Button
                  kind="primary"
                  onClick={() => handleNewDomain("empty_state")}
                  disabled={isMobile || !canManageDomains}
                >
                  Add domain
                </Button>
              }
            >
              Add a domain to increase the credibility of your messages. <br /> You can use it later in reply addresses.
            </EmptyState>
          </Flex>
        ) : (
          <Block>
            <Button kind="secondary" onClick={() => handleNewDomain("main")} disabled={isMobile || !canManageDomains}>
              Add domain
            </Button>
          </Block>
        )}
        {!canManageDomains && <NotAllowedBanner section={EmailSettingsSubsection.Domains} />}
        {isMobile && (
          <Alert kind="warning" css={{ maxWidth: "800px" }}>
            <Text bold>Adding domains unavailable on mobile devices</Text>
            <Text>Switch to the browser on your desktop device to add your domain.</Text>
          </Alert>
        )}
        {maxDomainsReached && <UpsellAlert source="emailDomains" />}
        {domains.length > 0 && (
          <DomainsList
            setModalAction={setModalAction}
            openDeleteModal={openDeleteModal}
            canManageDomains={canManageDomains}
          />
        )}
      </Flex>
    </>
  );
};

export const DomainsSection = withBreakpoints(DomainsSectionComponent);
